import { useState } from 'react'
import { cn } from '@/lib/utils'
import type {
  EstimatorAttrition, EstimatorBudget, EstimatorDesign, EstimatorSeparation, EstimatorSubgroup,
  EstimatorWeightCell,
} from '../api'
import { count, pct, pp, probability, signedPp } from './scale'

/**
 * The apparatus — everything beside the figures.
 *
 * ## Why this is a column of notes and not a rail of controls
 *
 * The other two consoles in this platform put lists beside their figure and let the reader
 * brush with them. This one deliberately does not. The estimator's figures show ONE question on
 * ONE poll, and there is nothing to filter: a poll's ballot has six options and all six are
 * already drawn. What a reader needs beside it is the apparatus of the estimate — what produced
 * it, what it cost in precision, what the field left out, and which slices it can and cannot be
 * asked about. That is a column of notes, which is what a statistical paper puts there too.
 */

/** A collapsible, capped section. The panels here can run long; the column must not. */
export function Panel({ figure, title, note, children, storageKey, maxBodyHeight = 0,
                       startClosed = false }: {
  figure?: string
  title: string
  note?: React.ReactNode
  children: React.ReactNode
  storageKey?: string
  maxBodyHeight?: number
  /**
   * Shut on a first visit, until the reader asks.
   *
   * <p>For the methodological panels. The sentences at the top of the board already say what
   * they contain — "the poll is as accurate as one with about 900 interviews, not 1,200" is
   * the design-cost plate in words — so leaving them open presents a candidate with four
   * instruments explaining a thing they have already been told. Closed, they are drill-down;
   * open by default, they are the reason the board looked like a paper.</p>
   */
  startClosed?: boolean
}) {
  const [open, setOpen] = useState(() => {
    if (!storageKey) return !startClosed
    try {
      const stored = window.localStorage.getItem(`axis.estimator.${storageKey}`)
      if (stored === null) return !startClosed
      return stored !== 'closed'
    } catch {
      return true
    }
  })

  const toggle = () => setOpen((was) => {
    const next = !was
    if (storageKey) {
      try {
        window.localStorage.setItem(`axis.estimator.${storageKey}`, next ? 'open' : 'closed')
      } catch { /* a preference is never worth failing the interaction over */ }
    }
    return next
  })

  const bodyId = `estimator-${storageKey ?? title.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <section className="border-b px-4 py-3.5" style={{ borderColor: 'var(--est-rule)' }}>
      <header className="mb-2.5 flex items-baseline justify-between gap-2">
        {storageKey ? (
          <button type="button" onClick={toggle} aria-expanded={open} aria-controls={bodyId}
                  className="flex items-center gap-1.5 text-left">
            <span aria-hidden
                  className={cn('inline-block font-mono text-[8px] leading-none',
                    'transition-transform', open ? 'rotate-90' : 'rotate-0')}
                  style={{ color: 'var(--est-ink-faint)' }}>▶</span>
            <h2 className="axis-riso-eyebrow">
              {figure ? <span style={{ opacity: 0.6 }}>{figure} · </span> : null}{title}
            </h2>
          </button>
        ) : (
          <h2 className="axis-riso-eyebrow">
            {figure ? <span style={{ opacity: 0.6 }}>{figure} · </span> : null}{title}
          </h2>
        )}
        {note ? <span className="axis-riso-mini tabular-nums">{note}</span> : null}
      </header>
      <div id={bodyId} hidden={!open}
           className={cn(maxBodyHeight > 0 && 'axis-riso-scroll overflow-y-auto')}
           style={maxBodyHeight > 0 ? { maxHeight: maxBodyHeight } : undefined}>
        {children}
      </div>
    </section>
  )
}

export function Stat({ label, value, note, tone }: {
  label: string
  value: React.ReactNode
  note?: string
  tone?: 'a' | 'b' | 'good'
}) {
  const ink = tone === 'b' ? 'var(--est-b)'
    : tone === 'good' ? 'var(--est-good)'
    : tone === 'a' ? 'var(--est-a)'
    : 'var(--est-ink)'
  return (
    <div className="flex flex-col items-end gap-0.5" title={note}>
      <i className="axis-riso-eyebrow not-italic">{label}</i>
      <b className="font-mono text-[15px] font-normal tabular-nums" style={{ color: ink }}>
        {value}
      </b>
    </div>
  )
}

// ── the reading ──────────────────────────────────────────────────────────────

/**
 * The verdict, in a sentence somebody can repeat out loud.
 *
 * ## Why the two tests are named separately
 *
 * When the intervals overlap and the gap is still separated — which is the common case and the
 * reason this board exists — a reader who has been taught "if the error bars overlap it is a
 * tie" will look at the figure above and conclude the opposite of what the poll says. Saying so
 * explicitly, in the case where it happens, is the only way the figure can be trusted; and in
 * the case where they agree, saying nothing extra is what keeps the sentence short.
 */
export function Reading({ separation, budget }: {
  separation: EstimatorSeparation | null
  budget: EstimatorBudget | null
}) {
  if (!separation) {
    return (
      <p className="text-[12px] leading-relaxed" style={{ color: 'var(--est-ink-dim)' }}>
        Only one option is published on this question, so there is no lead to test.
      </p>
    )
  }
  const separated = separation.separated === true
  const contradiction = separated && separation.intervalsOverlap === true

  return (
    <>
      <p className="mb-3 text-[15px] leading-snug" style={{ color: 'var(--est-ink)' }}>
        <b className="font-medium">{separation.aLabel}</b> leads by{' '}
        <b className="font-mono font-normal tabular-nums" style={{ color: 'var(--est-a)' }}>
          {pp(separation.gap)}
        </b>{' '}
        {separation.gapMoe95 !== null ? <>± {pp(separation.gapMoe95)} </> : null}
        —{' '}
        {separated
          ? <>the lead holds with probability{' '}
              <b className="font-mono font-normal tabular-nums"
                 style={{ color: 'var(--est-good)' }}>
                {probability(separation.leadProbability)}
              </b>.</>
          : <>which the poll <b className="font-medium" style={{ color: 'var(--est-b)' }}>
              cannot separate from no difference at all</b>.</>}
      </p>

      {contradiction ? (
        <p className="mb-3 border-l pl-3 text-[11.5px] leading-relaxed"
           style={{ color: 'var(--est-ink-dim)', borderColor: 'var(--est-b)' }}>
          Their own intervals overlap, so a chart with error bars would look like a tie. It is
          not one. The two shares come from a single sample — a respondent counted for one is a
          respondent not counted for the other — and that negative correlation makes the
          difference better measured than either share. The overlap test is the wrong test, and
          it is the one almost every published chart invites.
        </p>
      ) : null}

      {budget ? (
        <p className="text-[11.5px] leading-relaxed" style={{ color: 'var(--est-ink-dim)' }}>
          Quoted at {budget.atOptionLabel} ({pct(budget.atEstimate)}), this poll's margin is{' '}
          ±{pp(budget.realisedMoe)} rather than the ±{pp(budget.simpleRandomMoe)} its{' '}
          {count(budget.baseN)} interviews would have given under simple random sampling.
        </p>
      ) : null}
    </>
  )
}

// ── figure 3: what the design cost ───────────────────────────────────────────

/**
 * The realised margin against the one a simple random sample would have given.
 *
 * ## The number nobody publishes
 *
 * A methodology note quotes n and a margin of error. It almost never quotes the effective n,
 * and the gap between them is not small: at a design effect of 1.4 a poll of 1,200 has the
 * precision of a simple random sample of 870, and the 330 interviews in between were paid for
 * and given back by the design. Drawn as two brackets on one axis so the inflation is a
 * LENGTH, which is a thing the eye can compare, rather than a ratio it has to be told.
 */
export function DesignCost({ budget, design }: {
  budget: EstimatorBudget | null
  design: EstimatorDesign | null
}) {
  if (!budget) {
    return <p className="axis-riso-mini">No published estimate to quote a margin at.</p>
  }
  const scale = Math.max(budget.realisedMoe, budget.simpleRandomMoe) * 1.12
  const width = (moe: number) => `${(moe / scale) * 100}%`

  return (
    <div className="flex flex-col gap-2.5">
      <Bracket label="Simple random sample" value={`±${pp(budget.simpleRandomMoe)}`}
               width={width(budget.simpleRandomMoe)} outline />
      <Bracket label="This design" value={`±${pp(budget.realisedMoe)}`}
               width={width(budget.realisedMoe)} tone="b" />

      <dl className="mt-1 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-[11px]">
        <Row label="Interviews (n)">{count(budget.baseN)}</Row>
        <Row label="Effective (n_eff)">{count(budget.effectiveN)}</Row>
        <Row label="Given back by the design">
          <span style={{ color: 'var(--est-b)' }}>{count(budget.lostInterviews)}</span>
        </Row>
        <Row label="Design effect (deff)">{design?.deff?.toFixed(2) ?? '—'}</Row>
        <Row label="Margin inflation">
          {budget.inflation ? `×${budget.inflation.toFixed(2)}` : '—'}
        </Row>
      </dl>
    </div>
  )
}

function Bracket({ label, value, width, tone, outline }: {
  label: string
  value: string
  width: string
  tone?: 'a' | 'b'
  outline?: boolean
}) {
  const ink = tone === 'b' ? 'var(--est-b)' : 'var(--est-a)'
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-[11px]" style={{ color: 'var(--est-ink-dim)' }}>{label}</span>
        <b className="font-mono text-[11px] font-normal tabular-nums"
           style={{ color: outline ? 'var(--est-ink-dim)' : ink }}>{value}</b>
      </div>
      <span className="block h-[10px]" style={{ background: 'var(--est-paper-edge)' }}>
        <span className="block h-full"
              style={{
                width,
                background: outline ? 'transparent' : ink,
                opacity: outline ? 1 : 0.55,
                border: outline ? '1px dashed var(--est-ink-faint)' : `1px solid ${ink}`,
              }} />
      </span>
    </div>
  )
}

// ── figure 4: the chain the interval says nothing about ──────────────────────

/**
 * The field, as a chain of losses, with the AAPOR rates.
 *
 * ## Why this sits on a board about confidence intervals
 *
 * A 95% interval is a statement about sampling error and nothing else: how much this sample
 * would have moved if it had been drawn again from a frame we reached perfectly. It has nothing
 * to say about the people never contacted or the ones who refused — and on a poll where half
 * the eligible frame never became an interview, those are the larger question by a distance.
 * Putting the chain beside the interval is the only honest way to show the interval.
 */
export function Chain({ attrition }: { attrition: EstimatorAttrition | null }) {
  if (!attrition || attrition.suppressedReason) {
    return (
      <p className="text-[11.5px] leading-relaxed" style={{ color: 'var(--est-ink-faint)' }}>
        {attrition?.suppressedReason
          ?? 'No fieldwork has been recorded against this poll.'}
      </p>
    )
  }
  const top = Math.max(1, attrition.eligibleBase)
  const steps: [string, number, string | null][] = [
    ['Eligible frame', attrition.eligibleBase, null],
    ['Reached', attrition.reachedBase, rate('contact', attrition.contactRate1)],
    ['Took part', attrition.complete + attrition.partial, rate('coop', attrition.cooperationRate1)],
    ['Completed', attrition.complete, rate('RR1', attrition.responseRate1)],
    ['Analysed', attrition.analysed, null],
  ]

  return (
    <div className="flex flex-col gap-1.5">
      {steps.map(([label, value, note]) => (
        <div key={label} className="grid items-center gap-2"
             style={{ gridTemplateColumns: '86px 1fr 52px' }}>
          <span className="truncate text-[11px]" style={{ color: 'var(--est-ink-dim)' }}>
            {label}
          </span>
          <span className="block h-[11px]" style={{ background: 'var(--est-paper-edge)' }}>
            <span className="block h-full"
                  style={{
                    width: `${(value / top) * 100}%`,
                    background: 'var(--est-a)',
                    opacity: 0.34,
                  }} />
          </span>
          <span className="text-right font-mono text-[10px] tabular-nums"
                style={{ color: 'var(--est-ink-dim)' }}>{count(value)}</span>
          {note ? (
            <span className="col-start-2 col-end-4 -mt-0.5 axis-riso-mini">{note}</span>
          ) : null}
        </div>
      ))}

      <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <Row label="Refused">{count(attrition.refusal)}</Row>
        <Row label="Never contacted">{count(attrition.nonContact)}</Row>
        <Row label="Ineligible">{count(attrition.ineligible)}</Row>
        <Row label="Flagged">{count(attrition.flagged)}</Row>
      </div>

      <p className="mt-2 border-l pl-3 text-[11px] leading-relaxed"
         style={{ color: 'var(--est-ink-dim)', borderColor: 'var(--est-rule)' }}>
        The interval above covers <b className="font-medium">none</b> of this. It measures how
        much the sample would have moved if drawn again — not who was never reached.
      </p>
    </div>
  )
}

/** A rate, or a sentence saying why there is none. Never 0% for an empty denominator. */
function rate(name: string, value: number | null): string | null {
  return value === null ? `${name}: no denominator yet` : `${name} ${pct(value)}`
}

// ── provenance, weighting, thin cells ────────────────────────────────────────

export function Provenance({ design, poll }: {
  design: EstimatorDesign | null
  poll: { methodologyCode: string, statusCode: string, scopeUnitName: string | null }
}) {
  if (!design) return <p className="axis-riso-mini">Not weighted yet, so there is nothing to
    show here.</p>
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-[11px]">
      <Row label="Weighting">{design.methodCode ?? '—'}</Row>
      <Row label="Converged">
        {design.converged === null
          ? '—'
          : design.converged
            ? <span style={{ color: 'var(--est-good)' }}>yes</span>
            : <span style={{ color: 'var(--est-b)' }}>no — figures suppressed</span>}
      </Row>
      <Row label="Max deviation">
        {design.maxDeviation === null ? '—' : pp(design.maxDeviation, 2)}
      </Row>
      <Row label="Weights trimmed">
        {design.trimAt === null
          ? `${count(design.trimmedCount)}`
          : `${count(design.trimmedCount)} at ${design.trimAt.toFixed(1)}×`}
      </Row>
      <Row label="Mode">{poll.methodologyCode}</Row>
      <Row label="Scope">{poll.scopeUnitName ?? 'National'}</Row>
    </dl>
  )
}

/**
 * What the raking aimed at and what it hit.
 *
 * The deviation is signed. A cell that finished over its target and one that finished under it
 * are different fieldwork problems — the first is over-sampling somebody easy to reach, the
 * second is a group the field never got to — and an absolute value would render them the same.
 */
export function Weighting({ cells }: { cells: EstimatorWeightCell[] }) {
  if (!cells.length) return <p className="axis-riso-mini">No raking targets on this run.</p>
  const worst = Math.max(0.01, ...cells.map((c) => Math.abs(c.deviation ?? 0)))
  return (
    <div className="flex flex-col gap-1">
      {cells.map((cell) => {
        const dev = cell.deviation ?? 0
        return (
          <div key={`${cell.dimensionCode}/${cell.valueCode}`}
               className="grid items-center gap-2"
               style={{ gridTemplateColumns: '96px 1fr 58px' }}
               title={`Target ${pct(cell.targetShare)} · achieved ${pct(cell.achievedShare)}`}>
            <span className="truncate text-[11px]" style={{ color: 'var(--est-ink-dim)' }}>
              <span style={{ color: 'var(--est-ink-faint)' }}>{cell.dimensionCode}</span>{' '}
              {cell.valueCode}
            </span>
            {/* A deviation bar with zero at the CENTRE: the direction is the message. */}
            <span className="relative block h-[9px]"
                  style={{ background: 'var(--est-paper-edge)' }}>
              <i className="absolute inset-y-0 block w-px"
                 style={{ left: '50%', background: 'var(--est-rule-strong)', opacity: 0.5 }} />
              <i className="absolute inset-y-0 block"
                 style={{
                   left: dev >= 0 ? '50%' : undefined,
                   right: dev < 0 ? '50%' : undefined,
                   width: `${(Math.abs(dev) / worst) * 50}%`,
                   background: dev >= 0 ? 'var(--est-a)' : 'var(--est-b)',
                   opacity: 0.55,
                 }} />
            </span>
            <span className="text-right font-mono text-[10px] tabular-nums"
                  style={{ color: 'var(--est-ink-faint)' }}>{signedPp(cell.deviation, 1)}</span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * The crosstab cells, and where the poll stops being able to answer.
 *
 * Precision falls with the square root of the base, so a poll that is ±3 overall is ±9 in a
 * cell of a hundred and can separate nothing inside it. This panel exists so a reader sees
 * which slices the poll can be asked about BEFORE they ask.
 */
export function ThinCells({ subgroups }: { subgroups: EstimatorSubgroup[] }) {
  if (!subgroups.length) {
    return <p className="axis-riso-mini">No subgroup estimates on this question.</p>
  }
  const peak = Math.max(1, ...subgroups.map((s) => s.baseN))
  return (
    <div className="flex flex-col gap-1">
      {subgroups.map((s) => (
        <div key={`${s.dimensionCode}/${s.valueCode}`} className="grid items-center gap-2"
             style={{ gridTemplateColumns: '96px 1fr 62px' }}
             title={`n ${s.baseN}, effective ${count(s.effectiveN)}`
               + (s.worstMoe !== null ? ` · widest margin ±${pp(s.worstMoe)}` : '')
               + (s.suppressedCells > 0 ? ` · ${s.suppressedCells} cells not published` : '')}>
          <span className="truncate text-[11px]"
                style={{ color: s.thin ? 'var(--est-b)' : 'var(--est-ink-dim)' }}>
            <span style={{ color: 'var(--est-ink-faint)' }}>{s.dimensionCode}</span>{' '}
            {s.valueCode}
          </span>
          <span className="block h-[9px]" style={{ background: 'var(--est-paper-edge)' }}>
            <span className="block h-full"
                  style={{
                    width: `${(s.baseN / peak) * 100}%`,
                    background: s.thin ? 'var(--est-b)' : 'var(--est-a)',
                    opacity: 0.42,
                  }} />
          </span>
          <span className="text-right font-mono text-[10px] tabular-nums"
                style={{ color: 'var(--est-ink-faint)' }}>
            {s.worstMoe === null ? count(s.baseN) : `±${(s.worstMoe * 100).toFixed(1)}`}
          </span>
        </div>
      ))}
      <p className="mt-1.5 axis-riso-mini leading-relaxed">
        The figure is the widest 95% margin any option carries in that cell — not the average,
        because the option a reader asks about is usually the thinnest one.
      </p>
    </div>
  )
}

function Row({ label, children }: { label: string, children: React.ReactNode }) {
  return (
    <>
      <dt className="axis-riso-eyebrow">{label}</dt>
      <dd className="m-0 text-right font-mono tabular-nums"
          style={{ color: 'var(--est-ink)' }}>{children}</dd>
    </>
  )
}
